import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import {
    GET_DISPATCH_DELIVERY_SUMMARY_PAGE,
    DispatchDeliverySummaryPageResponse,
    DispatchDeliverySummaryGroupingMode
} from '../graphql/queries/getDispatchDeliverySummary';
import { GET_DISPATCH_DELIVERY_SUMMARY_PAGE_FOR_DSP_MANAGER } from '../graphql/queries/getDispatchDeliverySummaryForDspManager';
import { useWarehouseContext } from '../../../../../contexts/WarehouseContext';
import { SiteType } from '../../../../../graphql/types/SiteType';
import { isDspManager, isDspOwnerTeam } from '../../../../../utils/auth';

// 配置dayjs ISO周插件
dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);

export type DispatchDeliverySummaryDimension = 'DAY' | 'WEEK' | 'MONTH';

export interface IdOption {
    label: string;
    value: number;
}

export interface DispatchDeliverySummaryFilters {
    dspOptions?: IdOption[];
    warehouseOptions?: IdOption[];
    dateRange: [Dayjs, Dayjs] | null;
}

export const useDispatchDeliverySummary = (
    groupingMode: DispatchDeliverySummaryGroupingMode = 'NONE'
) => {
    const { currentWarehouse } = useWarehouseContext();
    
    // 初始化 filters：如果是 WHS 权限，立即设置 warehouseOptions，避免第一次不完整请求
    // 默认时间范围为一周
    const initialFilters = useMemo(() => {
        const defaultDateRange: [Dayjs, Dayjs] = [dayjs().subtract(7, 'day'), dayjs()];
        if (currentWarehouse?.siteType === SiteType.WAREHOUSE && currentWarehouse.name) {
            return {
                warehouseOptions: [
                    {
                        label: currentWarehouse.name,
                        value: Number(currentWarehouse.id)
                    }
                ],
                dateRange: defaultDateRange
            };
        }
        return { dateRange: defaultDateRange };
    }, [currentWarehouse]);
    
    const [filters, setFilters] = useState<DispatchDeliverySummaryFilters>(initialFilters);
    const [activeDimension, setActiveDimension] = useState<DispatchDeliverySummaryDimension>('DAY');
    const [pagination, setPagination] = useState({ page: 1, size: 20 });
    
    // 当 currentWarehouse 变化时，如果是 WHS 权限，更新 filters
    useEffect(() => {
        if (currentWarehouse?.siteType === SiteType.WAREHOUSE && currentWarehouse.name) {
            const warehouseId = Number(currentWarehouse.id);
            if (!filters.warehouseOptions?.some(opt => opt.value === warehouseId)) {
                setFilters(prev => ({
                    ...prev,
                    warehouseOptions: [
                        {
                            label: currentWarehouse.name ?? `#${warehouseId}`,
                            value: warehouseId
                        }
                    ]
                }));
            }
        }
    }, [currentWarehouse, filters.warehouseOptions]);

    // 从当前 warehouse context 获取 hubId（必传参数）
    const currentHubId = useMemo(() => {
        if (currentWarehouse) {
            if (currentWarehouse.siteType === SiteType.HUB) {
                return Number(currentWarehouse.id);
            }
            if (currentWarehouse.siteType === SiteType.WAREHOUSE && currentWarehouse.parentSiteId) {
                return Number(currentWarehouse.parentSiteId);
            }
        }
        return null;
    }, [currentWarehouse]);

    // 使用 ref 存储上一次的 variables 字符串，用于比较
    const prevVariablesRef = useRef<string>('');
    
    const variables = useMemo(() => {
        const [start, end] = filters.dateRange || [null, null];
        
        // 确保所有ID数组都是数字类型，并去重
        const dspIds = filters.dspOptions && filters.dspOptions.length > 0
            ? Array.from(new Set(filters.dspOptions.map(opt => opt.value)))
            : undefined;
        const warehouseIds = filters.warehouseOptions && filters.warehouseOptions.length > 0
            ? Array.from(new Set(filters.warehouseOptions.map(opt => opt.value)))
            : undefined;
        
        // hubId 从当前 warehouse context 获取，作为必传参数
        const hubIds = currentHubId ? [currentHubId] : undefined;
        
        // 根据dimension格式化日期
        let startDate: string | undefined;
        let endDate: string | undefined;
        
        if (start && end) {
            if (activeDimension === 'WEEK') {
                // 周格式：YYYY-WW (ISO周格式)
                const startWeekYear = start.isoWeekYear();
                const startWeek = start.isoWeek();
                const endWeekYear = end.isoWeekYear();
                const endWeek = end.isoWeek();
                startDate = `${startWeekYear}-${String(startWeek).padStart(2, '0')}`;
                endDate = `${endWeekYear}-${String(endWeek).padStart(2, '0')}`;
            } else if (activeDimension === 'MONTH') {
                // 月格式：YYYY-MM
                startDate = start.format('YYYY-MM');
                endDate = end.format('YYYY-MM');
            } else {
                // 日期格式：YYYY-MM-DD
                startDate = start.format('YYYY-MM-DD');
                endDate = end.format('YYYY-MM-DD');
            }
        }
        
        const result = {
            filter: {
                dspIds: dspIds,
                warehouseIds: warehouseIds,
                hubIds: hubIds,
                startDate: startDate,
                endDate: endDate,
                dimension: activeDimension,
                groupingMode
            },
            pageRequest: {
                page: pagination.page,
                size: pagination.size
            }
        };
        
        // 将 variables 序列化为字符串进行比较
        const variablesString = JSON.stringify(result);
        
        // 调试日志（只在真正变化时打印）
        if (prevVariablesRef.current !== variablesString) {
            console.log('DispatchDeliverySummary variables changed:', JSON.stringify(result, null, 2));
            prevVariablesRef.current = variablesString;
        }
        
        return result;
    }, [filters, pagination, activeDimension, currentHubId, groupingMode]);

    const shouldSkip =
        !currentHubId ||
        (currentWarehouse?.siteType === SiteType.WAREHOUSE &&
            (!filters.warehouseOptions || filters.warehouseOptions.length === 0));

    console.log('[DispatchSummary] query state:', {
        groupingMode,
        activeDimension,
        hubId: currentHubId,
        warehouseOptions: filters.warehouseOptions,
        shouldSkip
    });

    // DSP 经理走 ForDspManager 版接口；非 DSP 经理保持原逻辑。
    // 同时声明两套 useQuery（符合 React hook rules），运行时按 dspManagerStaff 切换 active 结果。
    // 新 query 用 GraphQL 别名让响应字段名与原版一致，下游读取代码零改动。
    const dspManagerStaff = isDspManager() || isDspOwnerTeam();

    const internalQuery = useQuery<DispatchDeliverySummaryPageResponse>(GET_DISPATCH_DELIVERY_SUMMARY_PAGE, {
        variables,
        skip: shouldSkip || dspManagerStaff,
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: false,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });
    const dspManagerQuery = useQuery<DispatchDeliverySummaryPageResponse>(GET_DISPATCH_DELIVERY_SUMMARY_PAGE_FOR_DSP_MANAGER, {
        variables,
        skip: shouldSkip || !dspManagerStaff,
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: false,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });
    const { data, loading, error } = dspManagerStaff ? dspManagerQuery : internalQuery;

    const handleFiltersChange = useCallback((next: Partial<DispatchDeliverySummaryFilters>) => {
        setFilters(prev => ({
            ...prev,
            ...next
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const handleDimensionChange = useCallback((dimension: DispatchDeliverySummaryDimension) => {
        setActiveDimension(dimension);
        setPagination(prev => ({ ...prev, page: 1 }));
        // 切换维度时清空日期范围，其他筛选框保持不变
        setFilters(prev => ({
            ...prev,
            dateRange: null
        }));
    }, []);

    const handlePageChange = useCallback((page: number, size?: number) => {
        setPagination(prev => ({
            page,
            size: size ?? prev.size
        }));
    }, []);

    const records = data?.getDispatchDeliverySummaryPage.items ?? [];
    const total = data?.getDispatchDeliverySummaryPage.total ?? 0;

    return {
        filters,
        setFilters: handleFiltersChange,
        activeDimension,
        setActiveDimension: handleDimensionChange,
        pagination,
        handlePageChange,
        records,
        total,
        loading,
        error,
        data
    };
};

